import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FiX } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import * as translator from '../../../../../utils/translator';
import WeightInput from './WeightInput';
import { fillWeight, WeightUnit } from '../../../../../utils/weightUnits';

export type SetGoalFormValues = {
	goalWeightKg: number;
};

interface SetGoalSheetProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: SetGoalFormValues) => void;
	currentKg: number | null;
	language: string;
	loading: boolean;
	submitError: string | null;
	weightUnit: WeightUnit;
	allowUnitChange: boolean;
	onWeightUnitChange: (unit: WeightUnit) => void;
}

/** Safe translate — missing CMS keys return null; always fall back to English copy. */
const t = (language: string, key: string, fallback: string): string => {
	const value = translator.translate(language, 'my_account', key);
	if (typeof value !== 'string' || !value || value === key || value.includes(key)) {
		return fallback;
	}
	return value;
};

const createSchema = (language: string, currentKg: number | null) =>
	z.object({
		goalWeightKg: z
			.number({
				invalid_type_error: t(language, 'CareProfile/SetGoal/InvalidWeight', 'Please enter a valid weight'),
			})
			.min(30, t(language, 'CareProfile/SetGoal/InvalidWeight', 'Please enter a valid weight'))
			.max(300, t(language, 'CareProfile/SetGoal/InvalidWeight', 'Please enter a valid weight'))
			.refine(
				(value) => currentKg == null || value < currentKg,
				t(language, 'CareProfile/SetGoal/MustBeLower', 'Goal weight must be lower than your current weight'),
			),
	});

const SetGoalSheet: React.FC<SetGoalSheetProps> = ({
	isOpen,
	onClose,
	onSubmit,
	currentKg,
	language,
	loading,
	submitError,
	weightUnit,
	allowUnitChange,
	onWeightUnitChange,
}) => {
	const schema = createSchema(language, currentKg);
	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
	} = useForm<SetGoalFormValues>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		defaultValues: { goalWeightKg: undefined as unknown as number },
	});
	// Bumped after each reset so WeightInput remounts from the reset value (it keeps its own text).
	const [inputKey, setInputKey] = React.useState(0);

	React.useEffect(() => {
		if (isOpen) {
			reset({ goalWeightKg: undefined as unknown as number });
			setInputKey((key) => key + 1);
		}
	}, [isOpen]);

	const currentHint = currentKg == null ? '' : fillWeight(
		t(language, 'CareProfile/SetGoal/CurrentHint', 'Your current weight is {{currentKg}} kg'),
		'{{currentKg}}', currentKg, weightUnit,
	);

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<Sheet.Header className="px-[21px] pt-4">
				<div className="flex items-center justify-between w-full">
					<p className="text-[16px] font-semibold text-care-plus-heading leading-[1.2]">
						{t(language, 'CareProfile/SetGoal/Title', 'Set a new goal')}
					</p>
					<button
						id="set-goal-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="flex items-center justify-center w-[31px] h-[31px] rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>
			</Sheet.Header>

			<Sheet.Body className="px-[21px] py-6">
				<form
					id="set-goal-form"
					className="flex flex-col gap-y-8"
					onSubmit={handleSubmit((values) => onSubmit(values))}
				>
					<div className="flex flex-col gap-y-3">
						{currentKg != null && (
							<p className="text-sm text-[#616975]">{currentHint}</p>
						)}
						<p className="text-sm text-[#616975]">
							{t(
								language,
								'CareProfile/SetGoal/Body',
								'Your progress graph will restart from your current weight. Past logs are kept.',
							)}
						</p>
						<Controller
							control={control}
							name="goalWeightKg"
							render={({ field }) => (
								<WeightInput
									key={inputKey}
									id="set-goal-weight"
									language={language}
									unit={weightUnit}
									allowUnitChange={allowUnitChange}
									onUnitChange={onWeightUnitChange}
									valueKg={field.value ?? undefined}
									onChangeKg={field.onChange}
									onBlur={field.onBlur}
									kgPlaceholder={t(language, 'CareProfile/SetGoal/WeightLabel', 'Goal weight (kg)')}
									errorMsg={errors.goalWeightKg?.message ?? ''}
								/>
							)}
						/>
						{submitError && <p className="text-red-500 text-xs">{submitError}</p>}
					</div>

					<button
						id="set-goal-submit"
						type="submit"
						disabled={loading}
						className="w-full bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white text-[16px] font-medium py-3.5 rounded-[16px] border-none transition-all duration-200 disabled:opacity-70 cursor-pointer"
					>
						{loading
							? (translator.translate(language, 'default', 'SubmitButton/loading_text') || 'Loading...')
							: t(language, 'CareProfile/SetGoal/Submit', 'Save new goal')}
					</button>
				</form>
			</Sheet.Body>
		</Sheet>
	);
};

export default SetGoalSheet;
